import { getOrigin } from '../config/siteConfig';
import { useSectionStore } from '../store/sectionStore';
import { isPointObjectClipped } from './objectClip';
import { resolveLayerKeyFromObject } from './pickLayerKey';
import { sectionPlanes } from './sectionMath';

const CLIP_EPS = 0.02;

function clipPlanesForPick(gl) {
  const fromGl = gl?.userData?.mdvClipPlanes;
  if (fromGl?.length) return fromGl;
  if (gl?.clippingPlanes?.length) return gl.clippingPlanes;
  const section = useSectionStore.getState();
  if (!section?.enabled) return [];
  return sectionPlanes(section, getOrigin());
}

function isClipSource(object) {
  let node = object;
  while (node) {
    if (node.userData?.mdvClipSource || node.userData?.mdvSkipClip) return true;
    node = node.parent;
  }
  return false;
}

/** Empty Group/Object3D raycast — children still get tested. */
export function defaultGroupRaycast() {}

/**
 * Returning false stops Three.js from raycasting children (r160+).
 * Hidden cached layers stay in memory but must not be pickable.
 */
export function skipHiddenSubtreeRaycast() {
  return false;
}

export function isObjectWorldVisible(object) {
  let node = object;
  while (node) {
    if (node.visible === false) return false;
    node = node.parent;
  }
  return true;
}

export function isPointClipped(point, planes) {
  if (!point || !planes || planes.length === 0) return false;
  for (let i = 0; i < planes.length; i += 1) {
    const plane = planes[i];
    if (plane && plane.distanceToPoint(point) < -CLIP_EPS) return true;
  }
  return false;
}

function isMaterialPickable(material) {
  if (!material) return true;
  const mats = Array.isArray(material) ? material : [material];
  return mats.some((mat) => {
    if (!mat || mat.visible === false) return false;
    if (typeof mat.opacity === 'number' && mat.opacity <= 0.01) return false;
    return true;
  });
}

/**
 * True when a raycast hit corresponds to something actually drawn:
 * world-visible, not section-clipped, and (if known) from an on layer.
 */
export function isPickableHit(hit, { gl, layerVisibility, skipClipFor } = {}) {
  const object = hit?.object;
  const point = hit?.point;
  if (!object || !point) return false;
  if (object.userData?.mdvSkipPick) return false;
  if (object.userData?.mdvPickPlane) return isObjectWorldVisible(object);
  if (!isObjectWorldVisible(object)) return false;
  if (!isMaterialPickable(object.material)) return false;
  const skipClip = Boolean(skipClipFor?.(object));
  if (!skipClip && isPointClipped(point, clipPlanesForPick(gl))) return false;
  if (!skipClip && !isClipSource(object)) {
    const section = useSectionStore.getState();
    if (
      section?.enabled &&
      section.clipParentUuid &&
      section.placeMode !== 'object' &&
      isPointObjectClipped(point, section.clipParentMode)
    ) {
      return false;
    }
  }
  if (layerVisibility) {
    const key = resolveLayerKeyFromObject(object, layerVisibility);
    if (key && layerVisibility[key] && !layerVisibility[key].visible) return false;
  }
  return true;
}

export function filterPickableHits(hits, state, layerVisibility, extra = {}) {
  if (!hits?.length) return hits || [];
  return hits.filter((hit) => isPickableHit(hit, { gl: state?.gl, layerVisibility, ...extra }));
}

export function firstPickableHit(event, { gl, layerVisibility, skipClipFor } = {}) {
  const hits = event?.intersections;
  if (Array.isArray(hits) && hits.length) {
    const found = hits.find((hit) => isPickableHit(hit, { gl, layerVisibility, skipClipFor }));
    if (found) return found;
  }
  if (isPickableHit(event, { gl, layerVisibility, skipClipFor })) return event;
  return null;
}
